import { useEffect, useId, useRef, useState } from 'react';
import type { OrderListQuery, OrderStatusFilter } from '@mypoz/shared';
import { ORDER_STATUS_FILTERS, hasActiveOrderListQuery } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon, Select, cn } from '@mypoz/ui';

import { ActiveFilterChip, FiltersPanel, FiltersToggle } from '@/components/ListFilters.js';
import { actionLinkClass } from '@/lib/styles.js';
import { useFormat } from '@/lib/format.js';
import { useSearchBox } from '@/lib/useSearchBox.js';

export interface OrderFilterBarProps {
  query: OrderListQuery;
  /** Every stage plus `all`, counted by the vendor with the same search and channel. */
  counts: Readonly<Record<OrderStatusFilter, number>>;
  /**
   * The channels to offer, as `{ id, label }` — the ones this account has shipped with, taken from
   * its orders, so every option selects something.
   */
  channels: readonly { id: string; label: string }[];
  /** `replace` keeps a keystroke out of browser history. */
  onChange: (next: OrderListQuery, options?: { replace?: boolean }) => void;
  onReset: () => void;
}

/** One status chip. Extracted so the active one can hold a ref and scroll itself into view. */
function StatusChip({
  status,
  count,
  active,
  label,
  onSelect,
}: {
  status: OrderStatusFilter;
  count: number;
  active: boolean;
  label: string;
  onSelect: (status: OrderStatusFilter) => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const fmt = useFormat();

  useEffect(() => {
    // `nearest` on both axes is doing real work: it leaves an already-visible chip alone, and it
    // never scrolls the page vertically to reach one that has scrolled sideways.
    if (active) ref.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [active]);

  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={active}
      onClick={() => onSelect(status)}
      className={cn(
        // min-h-11 holds the 44px touch target, released at lg where there is a pointer.
        'min-h-11 shrink-0 border-b-2 pb-2.5 text-sm whitespace-nowrap transition-colors lg:min-h-0',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        active
          ? 'border-brand font-semibold text-ink'
          : 'border-transparent text-muted hover:text-ink',
      )}
    >
      {label}{' '}
      <span className={cn('tabular-nums', active ? 'text-brand' : 'text-muted-soft')}>
        ({fmt.number(count)})
      </span>
    </button>
  );
}

/**
 * Search, the status chips, and the channel folded away behind a Filters button (MYP-128).
 *
 * The search and the chips — how a customer moves between "unpaid" and "shipped" — stay on
 * screen. The channel opens from the Filters button, so the list starts higher; while it is set, a
 * chip under the search says so and removes it in one tap, and the button counts it.
 *
 * Only filters the vendor applies itself are offered (MYP-126); the sort was dropped because the
 * vendor always answers newest first. The chips keep their zero counts rather than hiding: the
 * set of stages is fixed, and a `(0)` says where your orders are *not*.
 */
export function OrderFilterBar({
  query,
  counts,
  channels,
  onChange,
  onReset,
}: OrderFilterBarProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const channelId = useId();
  const panelId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const [searchText, setSearchText] = useSearchBox(query.search, (next) =>
    onChange({ ...query, search: next }, { replace: true }),
  );

  const active = hasActiveOrderListQuery(query);
  const channelLabel =
    query.channelId === 'all'
      ? undefined
      : (channels.find((channel) => channel.id === query.channelId)?.label ?? query.channelId);
  const folded = channelLabel === undefined ? 0 : 1;

  return (
    <div className="mb-4 grid gap-3">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-lg">
          <label htmlFor={searchId} className="sr-only">
            {t('orders.filters.searchLabel')}
          </label>
          <Icon
            name="search"
            size={20}
            className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-muted"
          />
          <input
            id={searchId}
            type="search"
            value={searchText}
            placeholder={t('orders.filters.searchPlaceholder')}
            onChange={(event) => setSearchText(event.target.value)}
            className={cn(
              // pr-14 clears the 44px clear button that sits at the right edge.
              'min-h-11 w-full rounded-lg bg-surface-alt pr-14 pl-11 text-sm text-ink',
              'transition-colors placeholder:text-muted hover:bg-line/40',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
            )}
          />
          {searchText ? (
            <button
              type="button"
              aria-label={t('orders.filters.clearSearch')}
              onClick={() => setSearchText('')}
              className="absolute top-1/2 right-1 flex size-11 -translate-y-1/2 items-center justify-center rounded-md text-muted transition-colors hover:bg-line/60 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:size-9"
            >
              <Icon name="close" size={18} />
            </button>
          ) : null}
        </div>

        <FiltersToggle
          open={panelOpen}
          activeCount={folded}
          controls={panelId}
          buttonRef={toggleRef}
          onToggle={() => setPanelOpen((open) => !open)}
        />

        {/* Reset appears only once there is something to reset, so it is never a dead control. */}
        {active ? (
          <button type="button" onClick={onReset} className={actionLinkClass('shrink-0')}>
            <Icon name="restart_alt" size={17} />
            <span className="sr-only sm:not-sr-only">{t('orders.filters.reset')}</span>
          </button>
        ) : null}
      </div>

      <FiltersPanel
        id={panelId}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        returnFocusTo={toggleRef}
      >
        <div className="w-full sm:w-56">
          <label htmlFor={channelId} className="mb-1.5 block text-sm text-muted">
            {t('orders.filters.channel')}
          </label>
          <Select
            id={channelId}
            value={query.channelId}
            onChange={(next) => onChange({ ...query, channelId: next })}
            options={[
              { value: 'all', label: t('orders.filters.channelAll') },
              ...channels.map((entry) => ({ value: entry.id, label: entry.label })),
            ]}
          />
        </div>
      </FiltersPanel>

      {!panelOpen && channelLabel !== undefined ? (
        <div className="flex flex-wrap gap-2">
          <ActiveFilterChip
            label={t('orders.filters.channel')}
            value={channelLabel}
            onRemove={() => onChange({ ...query, channelId: 'all' })}
          />
        </div>
      ) : null}

      {/*
        One line that scrolls sideways rather than wrapping, so the bar keeps a fixed height and
        the first order card starts in the same place on every screen width.
      */}
      <div className="-mx-1 flex gap-5 overflow-x-auto border-b border-line px-1">
        {ORDER_STATUS_FILTERS.map((status) => (
          <StatusChip
            key={status}
            status={status}
            count={counts[status]}
            active={query.status === status}
            label={status === 'all' ? t('orders.filters.all') : t(`orders.status.${status}`)}
            onSelect={(next) => onChange({ ...query, status: next })}
          />
        ))}
      </div>
    </div>
  );
}
